<script lang="ts">
import { useStore } from 'vuex';
import {
  AreAllRouteGroupsAllowed,
  GetAllowedAccountIds,
  GetAllowedCarrierIds,
  GetAllowedWholesalerIds,
  GetUserRole,
  IsUserAClient,
} from '@/utilities/AuthUtils';
import { useRouter } from 'vue-router';
import { AppState, Store } from '@/store';
import { onMounted } from 'vue';
import { AccountTabPermissionOrder } from '@/types/UserPermissions.js';
import { UserRole } from '@/types/UserRole.js';

export default {
  name: 'Home',
  beforeRouteEnter(to, from) {
    const user = Store.state.user;
    if (IsUserAClient(user) && to.name === 'Home') {
      const allowedAccounts = GetAllowedAccountIds(user);
      let defaultAccountTab = 'overview';

      // If the user is a client, we need to determine the default tab based on their permissions
      if (IsUserAClient(user)) {
        for (let tabPerm of AccountTabPermissionOrder) {
          if (user?.permissions.includes(tabPerm.permission)) {
            defaultAccountTab = tabPerm.tab;
            break;
          }
        }
      }

      if (allowedAccounts.length === 1) {
        if (Store.state.clientViewState.isClientViewEnabled && from.meta.isParentAccount) {
          return { path: `/parent/account/${allowedAccounts[0]}/overview`, hash: to.hash };
        }
        return { path: `/account/${allowedAccounts[0]}/${defaultAccountTab}`, hash: to.hash };
      }
      return { path: '/clients', params: to.params };
    }

    const userRole = GetUserRole(user);

    if (userRole === UserRole.Market) {
      const allowedCarrierIds = GetAllowedCarrierIds(user);
      if (allowedCarrierIds.length === 1) {
        return { path: `/carrier/${allowedCarrierIds[0]}/overview`, hash: to.hash };
      } else if (allowedCarrierIds.length > 1) {
        return { path: '/carriers', params: to.params };
      }

      const allowedWholesalerIds = GetAllowedWholesalerIds(user);
      if (allowedWholesalerIds.length === 1) {
        return { path: `/wholesaler-group/${allowedWholesalerIds[0]}/overview`, hash: to.hash };
      } else if (allowedWholesalerIds.length > 1) {
        return { path: '/wholesaler-group', params: to.params };
      }

      // if no carriers or wholesalers, where to redirect -> Unauthorized
    }
  },
};
</script>

<script setup lang="ts">
onMounted(() => {
  const user = useStore<AppState>().state.user;
  const router = useRouter();

  if (!user) {
    return router.replace({ path: '/logout' });
  }

  const userRole = GetUserRole(user);

  // For brokers and admins, redirect to the new landing page
  if (userRole === UserRole.Broker || userRole === UserRole.Admin) {
    return router.replace({ path: '/directory' });
  }

  // Keep existing logic for other user types
  const homePageOrder = ['/accounts', '/policy/explorer'];
  if (AreAllRouteGroupsAllowed(user)) {
    return router.replace(homePageOrder[0]);
  }

  const homePage =
    homePageOrder.find((to) => {
      const resolved = useRouter().resolve(to);
      return (
        resolved && resolved.meta?.routeGroup && (user.allowedRouteGroups || []).includes(resolved.meta.routeGroup)
      );
    }) || '/logout';

  return router.replace(homePage);
});
</script>

<template>
  <div>&nbsp;</div>
</template>
